import { Injectable } from '@angular/core';
import { Observable, combineLatest, map, of } from 'rxjs';
import { PropertyStore } from '@shared/services/property.store';
import { TenantStore } from '@shared/services/tenant.store';
import { ContractStore } from '@shared/services/contract.store';
import { LoanStore } from '@shared/services/loan.store';
import { toObservable } from '@angular/core/rxjs-interop';
import { computed } from '@angular/core';
import { Property } from '@shared/definitions/property';
import { Tenant, TenantType } from '@shared/definitions/tenant';
import { fullName } from '@shared/utils/person-validators';
import { Contract } from '@shared/definitions/contract';
import { Loan } from '@shared/definitions/loan';

export interface SearchResult {
  id: string;
  title: string;
  subtitle: string;
  type: 'property' | 'tenant' | 'contract' | 'loan';
  icon: string;
  route: string;
  entity: Property | Tenant | Contract | Loan;
}

export interface SearchResults {
  properties: SearchResult[];
  tenants: SearchResult[];
  contracts: SearchResult[];
  loans: SearchResult[];
  totalCount: number;
}

@Injectable({
  providedIn: 'root'
})
export class GlobalSearchService {

  // Cached signal→observable bridges must be created in injection context
  // (class-field initializers run during construction).
  private readonly propertyList$ = toObservable(computed(() => [...this.propertyStore.list()]));
  private readonly tenantList$ = toObservable(computed(() => [...this.tenantStore.list()]));
  private readonly contractList$ = toObservable(computed(() => [...this.contractStore.list()]));
  private readonly loanList$ = toObservable(computed(() => [...this.loanStore.list()]));

  constructor(
    private propertyStore: PropertyStore,
    private tenantStore: TenantStore,
    private contractStore: ContractStore,
    private loanStore: LoanStore
  ) {}

  search(query: string): Observable<SearchResults> {
    if (!query || query.trim().length < 2) {
      return of({
        properties: [],
        tenants: [],
        contracts: [],
        loans: [],
        totalCount: 0
      });
    }

    const searchTerm = query.toLowerCase().trim();

    return combineLatest([
      this.propertyList$,
      this.tenantList$,
      this.contractList$,
      this.loanList$
    ]).pipe(
      map(([properties, tenants, contracts, loans]) => {
        const propertyResults = this.searchProperties(properties, searchTerm);
        const tenantResults = this.searchTenants(tenants, searchTerm);
        const contractResults = this.searchContracts(contracts, searchTerm, properties, tenants);
        const loanResults = this.searchLoans(loans, searchTerm, properties);

        return {
          properties: propertyResults,
          tenants: tenantResults,
          contracts: contractResults,
          loans: loanResults,
          totalCount: propertyResults.length + tenantResults.length + contractResults.length + loanResults.length
        };
      })
    );
  }

  private searchProperties(properties: Property[], searchTerm: string): SearchResult[] {
    return properties
      .filter(property => this.matchesPropertySearch(property, searchTerm))
      .map(property => ({
        id: property.propertyUID,
        title: property.name,
        subtitle: property.address?.line1 ? 
          `${property.address.line1}, ${property.address.city}` : 
          'No address',
        type: 'property' as const,
        icon: 'home',
        route: `/properties/${property.propertyUID}`,
        entity: property
      }))
      .slice(0, 5); // Limit to 5 results per category
  }

  private searchTenants(tenants: Tenant[], searchTerm: string): SearchResult[] {
    return tenants
      .filter(tenant => this.matchesTenantSearch(tenant, searchTerm))
      .map(tenant => {
        const title = tenant.tenantType === TenantType.Professional && tenant.companyName ? 
          tenant.companyName : 
          tenant.tenantName || 'Unknown Tenant';
        
        const subtitle = tenant.contacts && tenant.contacts.length > 0 ?
          fullName(tenant.contacts[0].firstName, tenant.contacts[0].lastName) ||
          tenant.contacts[0].email || 'No contact info' :
          'No contact info';

        return {
          id: tenant.tenantUID,
          title,
          subtitle,
          type: 'tenant' as const,
          icon: tenant.tenantType === TenantType.Professional ? 'business' : 'person',
          route: `/tenants/${tenant.tenantUID}`,
          entity: tenant
        };
      })
      .slice(0, 5);
  }

  private searchContracts(contracts: Contract[], searchTerm: string, properties: Property[], tenants: Tenant[]): SearchResult[] {
    return contracts
      .filter(contract => this.matchesContractSearch(contract, searchTerm, properties, tenants))
      .map(contract => {
        const property = properties.find(p => p.propertyUID === contract.propertyUID);
        const tenant = tenants.find(t => t.tenantUID === contract.tenantUID);
        
        const title = property ? property.name : 'Unknown Property';
        const subtitle = tenant ? 
          (tenant.tenantType === TenantType.Professional && tenant.companyName ? 
            tenant.companyName : 
            tenant.tenantName || 'Unknown Tenant') : 
          'Unknown Tenant';

        return {
          id: contract.contractUID,
          title,
          subtitle: `Contract with ${subtitle}`,
          type: 'contract' as const,
          icon: 'description',
          route: `/contracts/detail/${contract.contractUID}`,
          entity: contract
        };
      })
      .slice(0, 5);
  }

  private searchLoans(loans: Loan[], searchTerm: string, properties: Property[]): SearchResult[] {
    return loans
      .filter(loan => this.matchesLoanSearch(loan, searchTerm, properties))
      .map(loan => {
        const property = properties.find(p => p.propertyUID === loan.propertyUID);
        const title = loan.loanProvider?.name || 'Unknown Lender';
        const subtitle = property ? 
          `Loan for ${property.name}` : 
          'Property not found';

        return {
          id: loan.loanUID,
          title,
          subtitle,
          type: 'loan' as const,
          icon: 'account_balance',
          route: `/loans/detail/${loan.loanUID}`,
          entity: loan
        };
      })
      .slice(0, 5);
  }

  private matchesPropertySearch(property: Property, searchTerm: string): boolean {
    const searchableFields = [
      property.name,
      property.address?.line1,
      property.address?.city,
      property.type,
      property.buildingInfo?.buildingName
    ].filter(Boolean).map(field => field!.toLowerCase());

    return searchableFields.some(field => field.includes(searchTerm));
  }

  private matchesTenantSearch(tenant: Tenant, searchTerm: string): boolean {
    const searchableFields = [
      tenant.tenantName,
      tenant.companyName,
      tenant.vatNumber,
      ...(tenant.contacts?.flatMap(contact => [
        contact.firstName,
        contact.lastName,
        contact.email,
        contact.phone
      ]) || [])
    ].filter(Boolean).map(field => field!.toLowerCase());

    return searchableFields.some(field => field.includes(searchTerm));
  }

  private matchesContractSearch(contract: Contract, searchTerm: string, properties: Property[], tenants: Tenant[]): boolean {
    const property = properties.find(p => p.propertyUID === contract.propertyUID);
    const tenant = tenants.find(t => t.tenantUID === contract.tenantUID);
    
    const searchableFields = [
      contract.status,
      property?.name,
      tenant?.tenantName,
      tenant?.companyName
    ].filter(Boolean).map(field => field!.toLowerCase());

    return searchableFields.some(field => field.includes(searchTerm));
  }

  private matchesLoanSearch(loan: Loan, searchTerm: string, properties: Property[]): boolean {
    const property = properties.find(p => p.propertyUID === loan.propertyUID);
    
    const searchableFields = [
      loan.loanProvider?.name,
      loan.loanType,
      property?.name
    ].filter(Boolean).map(field => field!.toLowerCase());

    return searchableFields.some(field => field.includes(searchTerm));
  }
} 